<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import ChatHeader from './ChatHeader.vue';
import ChatMessage from './ChatMessage.vue';
import ChoiceTray from './ChoiceTray.vue';
import ChatComposer from './ChatComposer.vue';
import TypingIndicator from './TypingIndicator.vue';
import ChatAvatar from './ChatAvatar.vue';

const props = defineProps({
  theme: { type: Object, required: true },
  messages: { type: Array, default: () => [] },
  choices: { type: Object, default: null },
  typing: { type: Boolean, default: false },
  // idle (not started yet) | connecting | ready | error
  status: { type: String, default: 'ready' },
  subtitle: { type: String, default: '' },
  whatsappUrl: { type: String, default: '' },
  closable: { type: Boolean, default: true },
  t: { type: Function, required: true },
});

const emit = defineEmits(['close', 'send', 'choose', 'retry', 'start']);

const scroller = ref(null);
const tray = ref(null);
const composer = ref(null);

const grouped = computed(() =>
  props.messages.map((message, index) => {
    const side = item => (item?.kind === 'visitor' ? 'visitor' : item?.kind);
    const previous = props.messages[index - 1];
    const next = props.messages[index + 1];
    return {
      message,
      groupStart:
        !previous || side(previous) !== side(message) || !!previous.submitted,
      groupEnd: !next || side(next) !== side(message) || !!message.submitted,
    };
  })
);

const placeholder = computed(() => {
  if (props.choices) return props.t('PLACEHOLDER_WITH_CHOICES');
  return props.theme.input_placeholder || props.t('PLACEHOLDER');
});

// New messages glide in; history loading and late-sizing media jump, since a smooth scroll that is
// still running when the content grows again stops short of the bottom.
const scrollToEnd = async (behavior = 'auto') => {
  await nextTick();
  const el = scroller.value;
  // A hidden tab never runs a smooth scroll, so a reply that lands while the visitor is elsewhere
  // would leave the list parked above it. Only animate when someone can see it.
  const visible = document.visibilityState === 'visible';
  if (el)
    el.scrollTo({
      top: el.scrollHeight,
      behavior: visible ? behavior : 'auto',
    });
};

watch(
  () => [
    props.messages.length,
    props.typing,
    props.choices?.id,
    props.messages.at(-1)?.submitted,
  ],
  ([length], previous) =>
    scrollToEnd(previous && length - previous[0] === 1 ? 'smooth' : 'auto'),
  { immediate: true }
);

const chooseByNumber = number => tray.value?.chooseByNumber(number);

defineExpose({ focus: () => composer.value?.focus() });
</script>

<template>
  <section
    class="flex h-full w-full flex-col overflow-hidden bg-canvas text-ink"
    :aria-label="theme.title"
  >
    <ChatHeader
      :theme="theme"
      :subtitle="subtitle"
      :whatsapp-url="whatsappUrl"
      :closable="closable"
      :t="t"
      @close="emit('close')"
    />

    <div
      ref="scroller"
      class="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4 pt-3"
      aria-live="polite"
    >
      <div
        v-if="
          status === 'idle' ||
          (status === 'ready' && !messages.length && !typing)
        "
        class="flex h-full flex-col items-center justify-center px-6 py-8 text-center"
      >
        <ChatAvatar
          :src="theme.avatar_url"
          :name="theme.title"
          size="lg"
          class="shadow-sm"
        />
        <p class="mt-4 text-[17px] font-semibold tracking-[-0.01em]">
          {{ theme.title }}
        </p>
        <p
          v-if="theme.subtitle"
          class="mt-1 max-w-[260px] text-[13.5px] leading-relaxed text-muted"
        >
          {{ theme.subtitle }}
        </p>
        <button
          v-if="status === 'idle'"
          type="button"
          class="mt-6 rounded-full bg-primary px-6 py-3 text-[14.5px] font-semibold text-on-primary shadow-launcher outline-none transition hover:brightness-110 focus-visible:ring-4 focus-visible:ring-primary/30"
          @click="emit('start')"
        >
          {{ theme.launcher_label || t('START') }}
        </button>
      </div>

      <div
        v-else-if="status === 'error'"
        class="flex h-full flex-col items-center justify-center px-8 text-center"
      >
        <p class="text-[14px] leading-relaxed text-muted">
          {{ t('CONNECTION_ERROR') }}
        </p>
        <button
          type="button"
          class="mt-4 rounded-full border border-line bg-surface px-5 py-2.5 text-[14px] font-semibold text-ink outline-none hover:border-primary/40 focus-visible:ring-4 focus-visible:ring-primary/20"
          @click="emit('start')"
        >
          {{ t('TRY_AGAIN') }}
        </button>
      </div>

      <template v-else>
        <ChatMessage
          v-for="item in grouped"
          :key="item.message.id || item.message.echoId"
          :message="item.message"
          :group-start="item.groupStart"
          :group-end="item.groupEnd"
          :theme="theme"
          :t="t"
          @retry="emit('retry', $event)"
          @media-loaded="scrollToEnd()"
        />
        <TypingIndicator
          v-if="typing || status === 'connecting'"
          :theme="theme"
          :t="t"
        />
      </template>
    </div>

    <ChoiceTray
      v-if="choices"
      ref="tray"
      :key="choices.id"
      :message="choices"
      :t="t"
      @choose="(message, option) => emit('choose', message, option)"
    />

    <ChatComposer
      v-if="status === 'ready' || status === 'connecting'"
      ref="composer"
      :class="choices ? '' : 'border-t border-line'"
      :placeholder="placeholder"
      :t="t"
      @send="emit('send', $event)"
      @number="chooseByNumber"
    />
  </section>
</template>
